import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { STORAGE_KEY } from '@/lib/i18n/config';

export type Language = 'de' | 'en';

export interface UseLanguageReturn {
  /** Current language code */
  language: Language;
  /** Change the current language */
  changeLanguage: (lang: Language) => Promise<void>;
  /** Available languages */
  languages: Language[];
  /** Check if a language is currently active */
  isLanguage: (lang: Language) => boolean;
}

/**
 * Hook for managing application language
 *
 * Provides utilities to:
 * - Get current language
 * - Change language with persistence to localStorage
 * - Check available languages
 *
 * @example
 * ```tsx
 * const { language, changeLanguage, languages } = useLanguage();
 *
 * return (
 *   <select value={language} onChange={(e) => changeLanguage(e.target.value)}>
 *     {languages.map(lang => (
 *       <option key={lang} value={lang}>{lang.toUpperCase()}</option>
 *     ))}
 *   </select>
 * );
 * ```
 */
export function useLanguage(): UseLanguageReturn {
  const { i18n } = useTranslation();

  const changeLanguage = useCallback(
    async (lang: Language) => {
      await i18n.changeLanguage(lang);
      localStorage.setItem(STORAGE_KEY, lang);
    },
    [i18n]
  );

  const isLanguage = useCallback(
    (lang: Language) => i18n.language === lang,
    [i18n.language]
  );

  return {
    language: i18n.language as Language,
    changeLanguage,
    languages: ['de', 'en'],
    isLanguage,
  };
}
